import {
  useAudienceKeyboardShortcuts,
  useBuildColourMap,
  useFullscreen,
  usePresenterSync,
} from '@/hooks'

import { FullScreenSlideView } from '@/components'
import type { Language } from '@/types'
import { createFileRoute } from '@tanstack/react-router'
import { getLessonQueryOptions } from '@/requestObjects/getLessonQueryOptions'
import { getUserLessonQueryOptions } from '@/requestObjects'
import { useAuthHeaders } from '@/providers'
import { useQueries } from '@tanstack/react-query'
import { useSlideTimings } from '@/hooks/useSlideTimings'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

// Define the search params schema with Zod
const audienceSearchSchema = z.object({
  slide: z.number().default(1),
})

type AudienceSearch = z.infer<typeof audienceSearchSchema>

export const Route = createFileRoute('/_authRoutes/lesson/$lessonId/audience')({
  validateSearch: (search: Record<string, unknown>): AudienceSearch => {
    return audienceSearchSchema.parse(search)
  },
  component: Audience,
})

function Audience() {
  const { lessonId } = Route.useParams()
  const { slide } = Route.useSearch()
  const headers = useAuthHeaders()
  const {
    i18n: { language },
  } = useTranslation()
  const [{ data: userLessonPlan }, { data: lessonMetadata }] = useQueries({
    queries: [
      {
        ...getUserLessonQueryOptions({
          lessonId,
          headers,
          language: language as Language,
        }),
      },
      {
        ...getLessonQueryOptions({ lessonId, headers }),
      },
    ],
  })

  const slideImages = lessonMetadata?.s3_uris || []
  const totalSlides = slideImages.length

  const { isFullscreen, requestFullscreen, toggleFullscreen } = useFullscreen()
  const { stopPresentation } = usePresenterSync({
    lessonId,
    slide,
    totalSlides,
  })

  useAudienceKeyboardShortcuts({
    lessonId,
    slide,
    toggleFullscreen,
    totalSlides,
    onClosePresentation: stopPresentation,
  })

  const slideColorMap = useBuildColourMap({ userLessonPlan, slideImages })
  // track slide timings
  useSlideTimings({
    slideColorMap,
    userLessonPlan,
    lessonId,
  })

  return (
    <FullScreenSlideView
      currentSlide={slide}
      totalSlides={totalSlides}
      slideImages={slideImages}
      isFullscreen={isFullscreen}
      requestFullscreen={requestFullscreen}
    />
  )
}
